import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useOutsideClick,
} from '@chakra-ui/react';
import { FiCalendar, FiMoreHorizontal, FiUser, FiX } from 'react-icons/fi';

import { font, fontSize, neutral, radius, shadow, text } from 'styles/revampTokens';

import {
  taskFieldProperties,
  taskOutlineButtonProperties,
  taskPrimaryButtonProperties,
  taskQuietButtonProperties,
} from './taskControls';
import { ModelPerson } from './modelPeople';
import { TaskCapabilities } from './types';

type ActionName = 'reassign' | 'approve' | 'reopen' | 'close' | 'due';

const problemText = (error: unknown) =>
  error instanceof Error && error.message ? error.message : 'That change could not be saved.';

const TaskActions = ({
  variant,
  capabilities,
  assigneeUserIds = [],
  loadModelPeople,
  onReassign,
  onApprove,
  onReopen,
  onClose,
  onChangeDue,
}: {
  variant: 'menu' | 'review';
  capabilities: TaskCapabilities;
  assigneeUserIds?: number[];
  loadModelPeople?: () => Promise<ModelPerson[]>;
  onReassign: (assigneeUserIds: number[]) => Promise<void>;
  onApprove: (note: string) => Promise<void>;
  onReopen: (note: string) => Promise<void>;
  onClose: () => Promise<void>;
  onChangeDue: (dueAt: string) => Promise<void>;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [open, setOpen] = useState<ActionName | null>(null);
  const [people, setPeople] = useState<ModelPerson[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const loadModelPeopleReference = useRef(loadModelPeople);
  loadModelPeopleReference.current = loadModelPeople;
  const menuReference = useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref: menuReference,
    handler: () => setMenuOpen(false),
  });
  const assigneeKey = (assigneeUserIds ?? []).join(',');

  const close = () => {
    setOpen(null);
    setPeopleOpen(false);
    setProblem('');
    setNote('');
  };

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setProblem('');
    try {
      await work();
      close();
    } catch (error: unknown) {
      setProblem(problemText(error));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open !== 'reassign') return () => {};
    let cancelled = false;
    setLoadingPeople(true);
    setProblem('');
    setSelected(assigneeKey ? assigneeKey.split(',').map(Number) : []);
    const load = loadModelPeopleReference.current ?? (async () => [] as ModelPerson[]);
    load()
      .then((rows) => {
        if (!cancelled) setPeople(rows);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setPeople([]);
          setProblem(problemText(error));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPeople(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assigneeKey]);

  const menuItems = [
    capabilities.reassign
      ? {
          key: 'reassign',
          label: 'Reassign',
          icon: <Box as={FiUser} aria-hidden />,
          onSelect: () => setOpen('reassign'),
        }
      : null,
    capabilities.change_due
      ? {
          key: 'due',
          label: 'Change due date',
          icon: <Box as={FiCalendar} aria-hidden />,
          onSelect: () => setOpen('due'),
        }
      : null,
    capabilities.close
      ? {
          key: 'close',
          label: 'Close task',
          icon: <Box as={FiX} aria-hidden />,
          onSelect: () => setOpen('close'),
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  const chosen = people.filter((person) => selected.includes(person.userId));
  const chosenLabel =
    chosen.length > 0 ? chosen.map((person) => person.name).join(', ') : 'Choose people';

  const togglePerson = (userId: number) => {
    setSelected((current) =>
      current.includes(userId) ? current.filter((value) => value !== userId) : [...current, userId],
    );
  };

  const saveAssignees = () => {
    if (selected.length === 0) {
      setProblem('Choose at least one person.');
      return;
    }
    run(() => onReassign(selected));
  };

  const showMenu = variant === 'menu' && menuItems.length > 0;
  const showReview = variant === 'review' && (capabilities.approve || capabilities.reopen);
  if (!showMenu && !showReview && open === null) return null;

  return (
    <>
      {showMenu && (
        <Box ref={menuReference} position='relative' flexShrink={0}>
          <Button
            aria-label='Task actions'
            aria-expanded={menuOpen}
            aria-haspopup='menu'
            variant='unstyled'
            display='inline-flex'
            alignItems='center'
            justifyContent='center'
            w='32px'
            minW='32px'
            maxW='32px'
            h='32px'
            borderRadius='6px'
            color={text.primary}
            _hover={{ bg: neutral.surfaceHover }}
            onClick={() => setMenuOpen((current) => !current)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setMenuOpen(false);
            }}
          >
            <Box as={FiMoreHorizontal} aria-hidden />
          </Button>
          {menuOpen && (
            <Box
              role='menu'
              position='absolute'
              right={0}
              top='calc(100% + 4px)'
              zIndex={20}
              w='196px'
              bg={neutral.surface}
              border={`1px solid ${neutral.border}`}
              borderRadius={radius.overlay}
              boxShadow={shadow.overlay}
              overflow='hidden'
            >
              {menuItems.map((item) => (
                <Button
                  key={item.key}
                  role='menuitem'
                  variant='unstyled'
                  // The theme's default size is 288px wide and would overflow this 196px panel.
                  size='slider'
                  display='flex'
                  w='100%'
                  h='36px'
                  justifyContent='flex-start'
                  alignItems='center'
                  gap='8px'
                  pl='16px'
                  pr='12px'
                  borderRadius={0}
                  textAlign='left'
                  fontWeight={400}
                  fontSize='13px'
                  fontFamily={font.body}
                  color={text.primary}
                  _hover={{ bg: neutral.surfaceHover, color: text.primary }}
                  _active={{ bg: neutral.surfaceHover, color: text.primary }}
                  onClick={() => {
                    setMenuOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.icon}
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      )}
      {showReview && capabilities.reopen && (
        <Button size='sm' {...taskOutlineButtonProperties} onClick={() => setOpen('reopen')}>
          Send back
        </Button>
      )}
      {showReview && capabilities.approve && (
        <Button size='sm' {...taskPrimaryButtonProperties} onClick={() => setOpen('approve')}>
          Approve
        </Button>
      )}
      <Modal isOpen={open === 'reassign'} onClose={close} isCentered>
        <ModalOverlay />
        <ModalContent bg={neutral.surface} borderRadius={radius.surface} mx={4}>
          <ModalHeader fontFamily={font.display} fontSize={fontSize.subtitle} color={text.primary}>
            Reassign
          </ModalHeader>
          <ModalBody>
            <Text fontSize={fontSize.body} color={text.secondary} mb={3}>
              Choose people already linked to this model.
            </Text>
            <Box position='relative'>
              <Button
                aria-label='Choose people'
                aria-expanded={peopleOpen}
                aria-haspopup='listbox'
                size='sm'
                {...taskOutlineButtonProperties}
                fontWeight={400}
                isDisabled={loadingPeople}
                onClick={() => setPeopleOpen((current) => !current)}
              >
                {loadingPeople ? 'Loading people…' : chosenLabel}
              </Button>
              {peopleOpen && (
                <Box
                  role='listbox'
                  aria-label='People on this model'
                  mt={2}
                  bg={neutral.surface}
                  border={`1px solid ${neutral.border}`}
                  borderRadius={radius.control}
                  overflow='hidden'
                >
                  {people.map((person) => {
                    const picked = selected.includes(person.userId);
                    return (
                      <Button
                        key={person.userId}
                        role='option'
                        aria-selected={picked}
                        variant='ghost'
                        w='100%'
                        h='auto'
                        justifyContent='flex-start'
                        px='11px'
                        py='8px'
                        borderRadius={0}
                        fontWeight={400}
                        color={text.primary}
                        _hover={{ bg: neutral.surfaceHover, color: text.primary }}
                        onClick={() => togglePerson(person.userId)}
                      >
                        <Box textAlign='left'>
                          <Text fontSize={fontSize.body} color={text.primary}>
                            {picked ? '✓ ' : ''}
                            {person.name}
                          </Text>
                          {person.email && (
                            <Text fontSize={fontSize.label} color={text.secondary}>
                              {person.email}
                            </Text>
                          )}
                        </Box>
                      </Button>
                    );
                  })}
                  {!loadingPeople && people.length === 0 && !problem && (
                    <Text px='11px' py='8px' fontSize={fontSize.body} color={text.secondary}>
                      No one else is linked to this model.
                    </Text>
                  )}
                </Box>
              )}
            </Box>
            {problem && (
              <Text mt={3} fontSize={fontSize.body} color={text.primary} role='alert'>
                {problem}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size='sm' mr={2} {...taskQuietButtonProperties} onClick={close}>
              Cancel
            </Button>
            <Button
              size='sm'
              {...taskPrimaryButtonProperties}
              isDisabled={busy || loadingPeople}
              onClick={saveAssignees}
            >
              Save assignees
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={open === 'approve'} onClose={close} isCentered>
        <ModalOverlay />
        <ModalContent bg={neutral.surface} borderRadius={radius.surface} mx={4}>
          <ModalHeader fontFamily={font.display} fontSize={fontSize.subtitle} color={text.primary}>
            Approve
          </ModalHeader>
          <ModalBody>
            <Textarea
              aria-label='Approval note'
              placeholder='Optional note for the contributor'
              value={note}
              {...taskFieldProperties}
              onChange={(event) => setNote(event.target.value)}
            />
            {problem && (
              <Text mt={3} fontSize={fontSize.body} color={text.primary} role='alert'>
                {problem}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size='sm' mr={2} {...taskQuietButtonProperties} onClick={close}>
              Cancel
            </Button>
            <Button
              size='sm'
              {...taskPrimaryButtonProperties}
              isDisabled={busy}
              onClick={() => run(() => onApprove(note))}
            >
              Approve task
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={open === 'reopen'} onClose={close} isCentered>
        <ModalOverlay />
        <ModalContent bg={neutral.surface} borderRadius={radius.surface} mx={4}>
          <ModalHeader fontFamily={font.display} fontSize={fontSize.subtitle} color={text.primary}>
            Send back
          </ModalHeader>
          <ModalBody>
            <Textarea
              aria-label='Send back note'
              placeholder='What needs to change?'
              value={note}
              {...taskFieldProperties}
              onChange={(event) => setNote(event.target.value)}
            />
            {problem && (
              <Text mt={3} fontSize={fontSize.body} color={text.primary} role='alert'>
                {problem}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size='sm' mr={2} {...taskQuietButtonProperties} onClick={close}>
              Cancel
            </Button>
            <Button
              size='sm'
              {...taskPrimaryButtonProperties}
              isDisabled={busy || note.trim() === ''}
              onClick={() => run(() => onReopen(note.trim()))}
            >
              Send back
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={open === 'close'} onClose={close} isCentered>
        <ModalOverlay />
        <ModalContent bg={neutral.surface} borderRadius={radius.surface} mx={4}>
          <ModalHeader fontFamily={font.display} fontSize={fontSize.subtitle} color={text.primary}>
            Close task
          </ModalHeader>
          <ModalBody>
            <Text fontSize={fontSize.body} color={text.secondary}>
              Closing ends this task. It cannot be sent back after that.
            </Text>
            {problem && (
              <Text mt={3} fontSize={fontSize.body} color={text.primary} role='alert'>
                {problem}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size='sm' mr={2} {...taskQuietButtonProperties} onClick={close}>
              Cancel
            </Button>
            <Button
              size='sm'
              {...taskPrimaryButtonProperties}
              isDisabled={busy}
              onClick={() => run(() => onClose())}
            >
              Close task
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={open === 'due'} onClose={close} isCentered>
        <ModalOverlay />
        <ModalContent bg={neutral.surface} borderRadius={radius.surface} mx={4}>
          <ModalHeader fontFamily={font.display} fontSize={fontSize.subtitle} color={text.primary}>
            Change due date
          </ModalHeader>
          <ModalBody>
            <Input
              type='date'
              aria-label='New due date'
              value={dueDate}
              {...taskFieldProperties}
              onChange={(event) => setDueDate(event.target.value)}
            />
            {problem && (
              <Text mt={3} fontSize={fontSize.body} color={text.primary} role='alert'>
                {problem}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size='sm' mr={2} {...taskQuietButtonProperties} onClick={close}>
              Cancel
            </Button>
            <Button
              size='sm'
              {...taskPrimaryButtonProperties}
              isDisabled={busy || dueDate === ''}
              onClick={() => run(() => onChangeDue(`${dueDate}T17:00:00Z`))}
            >
              Save due date
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TaskActions;
